'use client';

import { c as _c } from "react/compiler-runtime";
import type { HofAreaSummary, HofOverviewRow } from '@/features/hof-dashboard/api/hof-overview';
import { Panel, SectionHeading, StatusPill } from '@/features/hof-dashboard/components/dashboard-ui';
import { HOF_DASHBOARD_AREA_LABELS, HOF_DASHBOARD_AREAS, type HofDashboardArea } from '@/features/hof-dashboard/constants';
import { useAcceptHofArea } from '@/features/hof-dashboard/hooks/use-accept-hof-area';
import { translate } from '@/features/hof-dashboard/texts';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { CheckCheck, Download } from 'lucide-react';
import type React from 'react';

/** The areas a reviewer accepts at once; material orders are answered one by one. */
const ACCEPTED_AT_ONCE = new Set<HofDashboardArea>(['infrastructure', 'program']);

/**
 * A Hof's row as a table where the dashboard has room for one; below that each Hof is a card
 * with its areas stacked, so a phone never scrolls sideways.
 */
const ROW_GRID = '@3xl:grid @3xl:grid-cols-[minmax(9rem,1.2fr)_repeat(3,minmax(0,1fr))_7rem] @3xl:items-start @3xl:gap-x-4';
const LINK_CLASS = 'text-conveniat-green inline-flex min-h-10 cursor-pointer items-center gap-1.5 text-sm font-semibold underline-offset-2 hover:underline disabled:cursor-wait disabled:opacity-50';
const AreaCell: React.FC<{
  hof: HofOverviewRow;
  area: HofDashboardArea;
  summary: HofAreaSummary;
  accepting: boolean;
  onAccept: () => void;
  locale: Locale;
}> = t0 => {
  const $ = _c(34);
  const {
    hof,
    area,
    summary,
    accepting,
    onAccept,
    locale
  } = t0;
  if (summary.forms === 0) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="text-gray-400">–</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const areaLabel = HOF_DASHBOARD_AREA_LABELS[area][locale];
  let t1;
  if ($[1] !== locale || $[2] !== summary.done || $[3] !== summary.forms) {
    t1 = translate("progressCount", locale, {
      done: summary.done,
      total: summary.forms
    });
    $[1] = locale;
    $[2] = summary.done;
    $[3] = summary.forms;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <p className="text-gray-900 tabular-nums">{t1}</p>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== locale || $[8] !== summary.overdue) {
    t3 = summary.overdue > 0 && <StatusPill tone="alert">{translate("overdueCount", locale, {
        n: summary.overdue
      })}</StatusPill>;
    $[7] = locale;
    $[8] = summary.overdue;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== locale || $[11] !== summary.toReview) {
    t4 = summary.toReview > 0 && <StatusPill tone="neutral">{translate("toReviewCount", locale, {
        n: summary.toReview
      })}</StatusPill>;
    $[10] = locale;
    $[11] = summary.toReview;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== locale || $[14] !== summary.done || $[15] !== summary.forms || $[16] !== summary.toAccept) {
    t5 = summary.done === summary.forms && summary.toAccept === 0 && <StatusPill tone="done">{translate("allAccepted", locale)}</StatusPill>;
    $[13] = locale;
    $[14] = summary.done;
    $[15] = summary.forms;
    $[16] = summary.toAccept;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t3 || $[19] !== t4 || $[20] !== t5) {
    t6 = <div className="flex flex-wrap gap-1">{t3}{t4}{t5}</div>;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== accepting || $[23] !== area || $[24] !== areaLabel || $[25] !== hof || $[26] !== locale || $[27] !== onAccept || $[28] !== summary.toAccept) {
    t7 = ACCEPTED_AT_ONCE.has(area) && summary.toAccept > 0 && <button type="button" className={LINK_CLASS} disabled={accepting} aria-label={translate("acceptAreaLabel", locale, {
      area: areaLabel,
      hof: hof.name
    })} onClick={() => {
      const question = translate("acceptAreaQuestion", locale, {
        n: summary.toAccept,
        hof: hof.name,
        area: areaLabel
      });
      if (globalThis.confirm(question)) {
        onAccept();
      }
    }}><CheckCheck className="h-4 w-4" aria-hidden={true} />{translate("acceptArea", locale)}</button>;
    $[22] = accepting;
    $[23] = area;
    $[24] = areaLabel;
    $[25] = hof;
    $[26] = locale;
    $[27] = onAccept;
    $[28] = summary.toAccept;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== t2 || $[31] !== t6 || $[32] !== t7) {
    t8 = <div className="space-y-1">{t2}{t6}{t7}</div>;
    $[30] = t2;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  return t8;
};

/**
 * Where every Hof stands, for the reviewers: per area how much is handed in, overdue and
 * waiting for the Ressort, with a way to accept a Hof's Infrastruktur or Programm at once and
 * to download everything a Hof handed in. A Hof's name opens its dashboard below.
 */
export const HofOverviewTable: React.FC<{
  hoefe: HofOverviewRow[];
  selectedHofId: string | undefined;
  onOpen: (hofId: string) => void;
  locale: Locale;
}> = t0 => {
  const $ = _c(14);
  const {
    hoefe,
    selectedHofId,
    onOpen,
    locale
  } = t0;
  const {
    accept,
    pending
  } = useAcceptHofArea(locale);
  let t1;
  if ($[0] !== locale) {
    t1 = translate("allHoefe", locale);
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <SectionHeading>{t1}</SectionHeading>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== accept || $[5] !== hoefe || $[6] !== locale || $[7] !== onOpen || $[8] !== pending || $[9] !== selectedHofId) {
    t3 = hoefe.length === 0 ? <p className="text-sm text-gray-500">{translate("noHoefe", locale)}</p> : <div className="relative -mx-5 max-h-[32rem] overflow-y-auto border-y border-gray-100 @xl:-mx-6"><div aria-hidden={true} className={cn("sticky top-0 z-10 hidden bg-gray-50 px-6 py-2 text-xs font-semibold tracking-wider text-gray-500 uppercase", ROW_GRID)}><span>{translate("hof", locale)}</span>{HOF_DASHBOARD_AREAS.map(area => <span key={area}>{HOF_DASHBOARD_AREA_LABELS[area][locale]}</span>)}<span>{translate("files", locale)}</span></div><ul className="divide-y divide-gray-100">{hoefe.map(hof => {
          const selected = hof.id === selectedHofId;
          return <li key={hof.id} className={cn("px-5 py-3 text-sm @xl:px-6", ROW_GRID, selected && "bg-green-50")}><div className="flex items-center justify-between gap-3 @3xl:contents"><button type="button" className={cn(LINK_CLASS, "text-left")} aria-label={translate("openHof", locale, {
                hof: hof.name
              })} aria-current={selected ? "true" : undefined} onClick={() => onOpen(hof.id)}>{hof.name}</button><div className="shrink-0 @3xl:order-last">{hof.files === 0 ? <span className="text-gray-400">–</span> : <a href={`/api/hof-dashboard/${encodeURIComponent(hof.id)}/files`} download={true} className={LINK_CLASS} aria-label={translate("downloadFiles", locale, {
                  hof: hof.name
                })}><Download className="h-4 w-4" aria-hidden={true} />{translate("zipFiles", locale, {
                    n: hof.files
                  })}</a>}</div></div><div className="mt-2 grid gap-3 @md:grid-cols-3 @3xl:contents">{HOF_DASHBOARD_AREAS.map(area_0 => <div key={area_0} className="min-w-0"><p className="text-xs font-semibold tracking-wider text-gray-500 uppercase @3xl:sr-only">{HOF_DASHBOARD_AREA_LABELS[area_0][locale]}</p><AreaCell hof={hof} area={area_0} summary={hof.areas[area_0]} accepting={pending?.hofId === hof.id && pending.area === area_0} onAccept={() => void accept(hof.id, area_0)} locale={locale} /></div>)}</div></li>;
        })}</ul></div>;
    $[4] = accept;
    $[5] = hoefe;
    $[6] = locale;
    $[7] = onOpen;
    $[8] = pending;
    $[9] = selectedHofId;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t2 || $[12] !== t3) {
    t4 = <Panel className="space-y-4">{t2}{t3}</Panel>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
};